import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  lazy,
  Suspense,
  createContext,
} from "react";
import Sidebar from "./components/Sidebar.tsx";
import AdminSidebar from "./components/AdminSidebar.tsx";
import Header from "./components/Header.tsx";
import { useAuth } from "./components/AuthProvider.tsx";
import type { Page, OmniSearchResultItem } from "./types.ts";
import InactivityModal from "./components/InactivityModal.tsx";
import OmniSearchModal from "./components/OmniSearchModal.tsx";
import AIAssistant from "./components/AIAssistant.tsx";
import { authEvents } from "./src/lib/authEvents.ts";
import WorkspaceRequired from "./components/WorkspaceRequired.tsx";

// Lazy Load Pages
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const Orders = lazy(() => import("./pages/Orders.tsx"));
const Products = lazy(() => import("./pages/Products.tsx"));
const Customers = lazy(() => import("./pages/Customers.tsx"));
const Marketing = lazy(() => import("./pages/Marketing.tsx"));
const Channels = lazy(() => import("./pages/Channels.tsx"));
const Reports = lazy(() => import("./pages/Reports.tsx"));
const Shipments = lazy(() => import("./pages/Shipments.tsx"));
const Transactions = lazy(() => import("./pages/Transactions.tsx"));
const Invoices = lazy(() => import("./pages/Invoices.tsx"));
const Inbox = lazy(() => import("./pages/Inbox.tsx"));
const KnowledgeHub = lazy(() => import("./pages/KnowledgeHub.tsx"));
const Settings = lazy(() => import("./pages/Settings.tsx"));
const ComingSoon = lazy(() => import("./pages/ComingSoon.tsx"));
const WarehouseInventory = lazy(() => import("./pages/WarehouseInventory.tsx"));
const ConnectChannel = lazy(() => import("./pages/ConnectChannel.tsx"));
const Forbidden = lazy(() => import("./pages/Forbidden.tsx"));
const NotAcceptable = lazy(() => import("./pages/NotAcceptable.tsx"));
const ServerError = lazy(() => import("./pages/ServerError.tsx"));
const PaymentGateways = lazy(() => import("./pages/PaymentGateways.tsx"));
const LoggedOut = lazy(() => import("./pages/auth/LoggedOut.tsx"));
const Login = lazy(() => import("./pages/Login.tsx"));
const SignUp = lazy(() => import("./pages/SignUp.tsx"));
const SignUpWelcome = lazy(() => import("./pages/SignUpWelcome.tsx"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword.tsx"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback.tsx"));
const OnboardingHome = lazy(() => import("./pages/OnboardingHome.tsx"));
const DesignSystem = lazy(() => import("./pages/DesignSystem.tsx"));
const PricingPage = lazy(() => import("./pages/Pricing.tsx"));
const ClientSupport = lazy(() => import("./pages/ClientSupport.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const TeamManagement = lazy(() => import("./pages/TeamManagement.tsx"));
const Billing = lazy(() => import("./pages/Billing.tsx"));
const Track = lazy(() => import("./pages/Track.tsx"));
const FinalizeWorkspace = lazy(() => import("./pages/FinalizeWorkspace.tsx"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients.tsx"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers.tsx"));
const AdminBilling = lazy(() => import("./pages/admin/AdminBilling.tsx"));
const SuperAdminBilling = lazy(
  () => import("./pages/admin/SuperAdminBilling.tsx"),
);
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory.tsx"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs.tsx"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders.tsx"));
const AdminReturns = lazy(() => import("./pages/admin/AdminReturns.tsx"));
const AdminMarketing = lazy(() => import("./pages/admin/AdminMarketing.tsx"));
const AdminChannels = lazy(() => import("./pages/admin/AdminChannels.tsx"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings.tsx"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport.tsx"));
const AddClient = lazy(() => import("./pages/admin/AddClient.tsx"));
const AddUser = lazy(() => import("./pages/admin/AddUser.tsx"));
const CreateCampaign = lazy(() => import("./pages/admin/CreateCampaign.tsx"));
const EditClient = lazy(() => import("./pages/admin/EditClient.tsx"));
const HealthDashboard = lazy(() => import("./pages/admin/HealthDashboard.tsx"));
const BulkSyncMonitor = lazy(() => import("./pages/admin/BulkSyncMonitor.tsx"));
const DLQConsole = lazy(() => import("./pages/admin/DLQConsole.tsx"));
const InventoryReconciliation = lazy(
  () => import("./pages/InventoryReconciliation.tsx"),
);
const OperationsCenter = lazy(() => import("./pages/OperationsCenter.tsx"));

const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const WARNING_TIMEOUT = 1 * 60 * 1000; // 1 minute warning

const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span className="text-gray-500 font-medium">Loading...</span>
    </div>
  </div>
);

// ─── Provisioning Guard Context ──────────────────────────────────────────
export const ProvisioningContext = createContext({
  setIsProvisioning: (_: boolean) => {},
});

// ─── Hoisted Canonical Route Registry & Metadata (S3) ────────────────────────
export const ALL_ADMIN_PAGES: readonly Page[] = [
  "Admin Dashboard",
  "Admin Clients",
  "Admin Users",
  "Admin Billing",
  "Admin Inventory",
  "Admin Orders",
  "Admin Returns",
  "Admin Marketing",
  "Admin Channels",
  "Admin Settings",
  "Admin Support",
  "Admin Logs",
  "Health",
  "Bulk Sync",
  "DLQ Recovery",
  "Operations Center",
  "Inventory Reconciliation",
  "Add Client",
  "Add User",
  "Create Campaign",
] as const;

export const isAdminPageName = (page: string): boolean =>
  (ALL_ADMIN_PAGES as readonly string[]).includes(page);

export const PAGE_PATH_MAP: Record<string, string> = {
  Login: "/",
  SignUp: "/signup",
  LoggedOut: "/logged-out",
  ForgotPassword: "/forgot-password",
  AuthCallback: "/auth/callback",
  Privacy: "/privacy",
  Terms: "/terms",
  Onboarding: "/onboarding",
  Dashboard: "/dashboard",
  Orders: "/orders",
  Products: "/products",
  Customers: "/customers",
  Marketing: "/marketing",
  Channels: "/channels",
  "Connect Channel": "/connect-channel",
  Reports: "/reports",
  Shipments: "/shipments",
  "Warehouse Inventory": "/inventory",
  Transactions: "/transactions",
  Invoices: "/invoices",
  "Payment Gateways": "/gateways",
  "Unified Inbox": "/inbox",
  "Knowledge Hub": "/knowledge",
  Settings: "/settings",
  Billing: "/billing",
  "Team Management": "/team",
  Support: "/support",
  FinalizeWorkspace: "/finalize",
  DesignSystem: "/design",
  "Admin Dashboard": "/admin/dashboard",
  "Admin Clients": "/admin/clients",
  "Admin Users": "/admin/users",
  "Admin Billing": "/admin/billing",
  "Admin Inventory": "/admin/inventory",
  "Admin Orders": "/admin/orders",
  "Admin Returns": "/admin/returns",
  "Admin Marketing": "/admin/marketing",
  "Admin Channels": "/admin/channels",
  "Admin Settings": "/admin/settings",
  "Admin Support": "/admin/support",
  "Admin Logs": "/admin/logs",
  Health: "/admin/health",
  "Bulk Sync": "/admin/bulk-sync",
  "DLQ Recovery": "/admin/dlq",
  "Operations Center": "/operations",
  "Inventory Reconciliation": "/inventory/reconciliation",
  "Add Client": "/admin/clients/add",
  "Add User": "/admin/users/add",
  "Create Campaign": "/admin/marketing/create",
};

export const resolvePageFromPath = (path: string): Page => {
  if (path === "/signup") return "SignUp";
  if (path === "/login") return "Login";
  if (path.startsWith("/track/")) return "Track";
  if (
    path.startsWith("/admin") ||
    path === "/operations" ||
    path === "/inventory/reconciliation"
  ) {
    return "Admin Dashboard"; // Evaluated by route guard after profile hydration
  }
  if (path.startsWith("/products/")) return "Products";
  if (path.startsWith("/orders/")) return "Orders";
  if (path.startsWith("/customers/")) return "Customers";
  if (path.startsWith("/invoices/")) return "Invoices";
  if (path.startsWith("/transactions/")) return "Transactions";
  if (path.startsWith("/marketing/")) return "Marketing";
  for (const [pageName, routePath] of Object.entries(PAGE_PATH_MAP)) {
    if (routePath !== "/" && path.startsWith(routePath))
      return pageName as Page;
  }
  return "Login";
};

const ProvisioningOverlay = ({ message }: { message: string }) => (
  <div className="fixed inset-0 z-[var(--z-critical)] flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-2xl">
    <div className="relative mb-8">
      <div className="animate-spin rounded-full h-16 w-16 border-2 border-white/10 border-t-red-500 shadow-2xl" />
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          src="/logo-cherry-icon.png"
          alt="Cherry Logo"
          className="w-8 h-8 object-contain"
        />
      </div>
    </div>
    <h2 className="text-xl font-bold text-white mb-2 tracking-tight">
      {message}
    </h2>
    <p className="text-slate-400 text-sm animate-pulse">
      Initializing your premium workspace...
    </p>

    {/* Decorative background glow */}
    <div className="absolute -bottom-24 -left-24 h-96 w-96 bg-red-600/20 rounded-full blur-[120px] pointer-events-none" />
    <div className="absolute -top-24 -right-24 h-96 w-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
  </div>
);

export default function AppContent(): JSX.Element {
  const {
    user,
    login,
    signUp,
    logout,
    isLoading: isAuthLoading,
    isProfileLoading,
    isSyncing,
    lastError,
    retryProfileFetch,
    authStatus,
  } = useAuth();

  const [isProvisioning, setIsProvisioning] = useState(false);
  const isTransitioning = isAuthLoading || isProfileLoading || isProvisioning;
  const isAdminRole = user
    ? ["admin", "super_admin"].includes(user.role || "")
    : false;

  // DEF-03: Resolve Initial Page and Track Navigation Intent
  const [activePage, setActivePage] = useState<Page>(() =>
    resolvePageFromPath(window.location.pathname),
  );
  const navigationIntentRef = useRef<"push" | "replace">("replace");

  // ⚡ GHOST IDENTITY PREVENTION
  // Ensure we don't accidentally evaluate routes while a profile is still loading
  // or while a provisioning sequence is active.

  // Hydrate Initial State from URL (Short IDs / NanoIDs)
  // This runs on first mount to restore state from bookmarked deep-link URLs
  useEffect(() => {
    const path = window.location.pathname;
    const deepLinkPattern =
      /^\/(?:products|orders|customers|invoices|transactions|marketing)\/([A-Za-z0-9_-]+)$/;
    const deepLinkMatch = path.match(deepLinkPattern);

    if (deepLinkMatch) {
      // The ID segment (NanoID or ShortID) for item-level deep links
      setInitialState({ initialShortId: deepLinkMatch[1] });
    }

    // Public Track Deep Link
    const trackMatch = path.match(/^\/track\/([A-Za-z0-9_-]+)$/);
    if (trackMatch) {
      setInitialState({ initialShortId: trackMatch[1] });
    }
  }, []); // Run once on mount

  useEffect(() => {
    // 🛡️ CRITICAL GUARD: Never evaluate redirection while the DB profile is still fetching.
    // This prevents "jumpy" UX where users are flickered through Onboarding.
    if (isProfileLoading || isSyncing) return;
    if (!user) return;

    const currentIsAdminPage = activePage.startsWith("Admin");
    const onAuthPage = ["Login", "SignUp"].includes(activePage);

    if (onAuthPage) {
      // 1. Re-check URL for Deep Link hydration (in case user just logged in on a deep link URL)
      const path = window.location.pathname;
      const deepLinkMatch = path.match(
        /^\/(products|orders|customers|invoices|transactions|marketing)\/([A-Za-z0-9_-]+)$/,
      );

      if (deepLinkMatch) {
        const module = deepLinkMatch[1];
        const id = deepLinkMatch[2];
        const targetPage = (module.charAt(0).toUpperCase() +
          module.slice(1)) as Page;

        // Skip hydration if the "ID" is actually just the tenant's workspace ID
        const isTenantId =
          id === user.client_nano_id ||
          id === user.nano_id ||
          id === String(user.id);

        if (!isTenantId) {
          console.log(
            `[AppContent] Restoring Deep Link after login: ${targetPage} #${id}`,
          );
          setInitialState({ initialNanoId: id });
          setActivePage(targetPage);
          return;
        } else {
          // Clean up the URL by stripping the tenant ID out
          window.history.replaceState(null, "", `/${module}`);
        }
      }

      // 2. Standard Role & Onboarding Redirection
      let target: Page = isAdminRole ? "Admin Dashboard" : "Dashboard";

      // Final verification for Client Onboarding
      const isSubscribed = ["active", "trialing"].includes(
        user.subscription_status || "",
      );
      const needsOnboarding =
        !isAdminRole && user.onboarding_status !== "completed" && !isSubscribed;

      if (needsOnboarding) {
        target = "Onboarding";
        const onboardingId = user.client_nano_id;
        if (onboardingId && window.location.pathname === "/onboarding") {
          window.history.replaceState(null, "", `/onboarding/${onboardingId}`);
        }
      }

      if (activePage !== target) {
        console.log(
          `[AppContent] Auto-navigating (${activePage} -> ${target})`,
        );
        setActivePage(target);
      }
    } else if (
      activePage === "Onboarding" &&
      user.onboarding_status === "completed"
    ) {
      // 🏃 EXIT ONBOARDING: If they are on Onboarding but DB says they are done, move them to Dashboard.
      const target = (isAdminRole ? "Admin Dashboard" : "Dashboard") as Page;
      if (activePage !== target) {
        console.log(`[AppContent] Onboarding complete. Moving to ${target}.`);
        setActivePage(target);
      }
    } else if (
      !isAdminRole &&
      user.onboarding_status !== "completed" &&
      activePage === "Dashboard"
    ) {
      // 🛑 FORCE ONBOARDING: If they are on Dashboard but should be on Onboarding, move them.
      // We skip this if they have an active or trialing subscription to avoid the loop.
      const isSubscribed = ["active", "trialing"].includes(
        user.subscription_status || "",
      );
      if (!isSubscribed && user.subscription_status !== "pending") {
        console.log(
          "[AppContent] Client user missing onboarding and no active subscription. Moving to Onboarding.",
        );
        setActivePage("Onboarding");
      }
    } else if (
      isAdminRole &&
      !currentIsAdminPage &&
      activePage === "Dashboard"
    ) {
      console.log(
        "[AppContent] Role upgrade detected. Correcting dashboard to Admin.",
      );
      setActivePage("Admin Dashboard");
    } else if (!isAdminRole && isAdminPageName(activePage)) {
      console.info(
        `[AppContent] Role alignment: redirecting non-admin from '${activePage}' to Client Dashboard.`,
      );
      setActivePage("Dashboard");
    }
  }, [
    user?.role,
    user?.onboarding_status,
    user?.subscription_status,
    activePage,
    isProfileLoading,
  ]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [initialState, setInitialState] = useState<any>({});
  const [isInactive, setIsInactive] = useState(false);
  const [editingClientId, setEditingClientId] = useState<number | null>(null);
  const [forbiddenError, setForbiddenError] = useState<string | null>(null); // New State

  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AUTH EVENT LISTENERS (Moved from AuthProvider to support Custom Router)
  useEffect(() => {
    // Handle 401 Unauthorized
    const unsubscribe401 = authEvents.on("auth:unauthorized", (data) => {
      console.log("[AppContent] 401 detected, logging out...");

      // 1. Logout via Context
      logout();

      // 2. Force Navigation to Login
      setActivePage("Login");

      // 3. Clear History (Optional but good)
      window.history.replaceState(null, "", "/login");
    });

    // Handle 403 Forbidden
    const unsubscribe403 = authEvents.on("auth:forbidden", (data) => {
      console.warn("[AppContent] 403 detected, access denied");

      // Show Error Modal
      setForbiddenError(
        data.resource
          ? `You don't have permission to access ${data.resource}`
          : "You don't have permission to perform this action",
      );

      // Auto-dismiss and redirect to Dashboard after 3s
      setTimeout(() => {
        setForbiddenError(null);
        setActivePage("Dashboard");
        window.history.replaceState(null, "", "/");
      }, 3000);
    });

    return () => {
      unsubscribe401();
      unsubscribe403();
    };
  }, [logout]);

  const handleLogout = useCallback(() => {
    logout();
    setActivePage("LoggedOut");
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    setIsInactive(false);
  }, [logout]);

  const resetInactivityTimer = useCallback(() => {
    if (!user) return;
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    setIsInactive(false);
    warningTimer.current = setTimeout(
      () => setIsInactive(true),
      INACTIVITY_TIMEOUT - WARNING_TIMEOUT,
    );
    inactivityTimer.current = setTimeout(handleLogout, INACTIVITY_TIMEOUT);
  }, [handleLogout, user]);

  useEffect(() => {
    const events = ["mousemove", "keydown", "mousedown", "touchstart"];
    if (user) {
      events.forEach((event) =>
        window.addEventListener(event, resetInactivityTimer),
      );
      resetInactivityTimer();
    }
    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer),
      );
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, [resetInactivityTimer, user]);

  useEffect(() => {
    const path = window.location.pathname;
    if (path === "/auth/callback") {
      setActivePage("AuthCallback");
    } else if (path === "/login") {
      window.history.replaceState(null, "", "/");
    } else if (path === "/privacy") {
      setActivePage("Privacy");
    } else if (path === "/terms") {
      setActivePage("Terms");
    } else if (path.startsWith("/onboarding")) {
      setActivePage("Onboarding");
    } else if (path === "/orders") {
      setActivePage("Orders");
    } else if (path === "/products") {
      setActivePage("Products");
    } else if (path === "/settings") {
      setActivePage("Settings");
    } else if (path === "/customers") {
      setActivePage("Customers");
    } else if (path === "/inbox") {
      setActivePage("Unified Inbox");
    } else if (path === "/marketing") {
      setActivePage("Marketing");
    } else if (path === "/channels") {
      setActivePage("Channels");
    } else if (path === "/connect-channel") {
      setActivePage("Connect Channel");
    } else if (path === "/reports") {
      setActivePage("Reports");
    } else if (path === "/shipments") {
      setActivePage("Shipments");
    } else if (path === "/inventory") {
      setActivePage("Warehouse Inventory");
    } else if (path === "/transactions") {
      setActivePage("Transactions");
    } else if (path === "/invoices") {
      setActivePage("Invoices");
    } else if (path === "/gateways") {
      setActivePage("Payment Gateways");
    } else if (path === "/knowledge") {
      setActivePage("Knowledge Hub");
    } else if (path === "/billing") {
      setActivePage("Billing");
    } else if (path === "/team") {
      setActivePage("Team Management");
    } else if (path === "/design") {
      setActivePage("DesignSystem");
    } else if (path === "/dashboard") {
      setActivePage("Dashboard");
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Enforce Logout State if on LoggedOut page
  useEffect(() => {
    if (activePage === "LoggedOut" && user) {
      console.log("[AppContent] Enforcing logout on LoggedOut page");
      logout();
    }
  }, [activePage, user, logout]);

  // initialState is now managed via handleNavigation (passed as onNavigate)
  // Removing aggressive auto-clear to allow URL sync to catch new IDs

  // DEF-05 & C6: Idempotent Single-Owner URL Reconciler with Intent Tracking
  useEffect(() => {
    if (!user || isProfileLoading || isProvisioning) return;
    const basePath = PAGE_PATH_MAP[activePage as string];
    if (!basePath) return;

    const isAuthPath = [
      "/",
      "/login",
      "/signup",
      "/logged-out",
      "/forgot-password",
      "/auth/callback",
      "/privacy",
      "/terms",
    ].includes(basePath);
    if (isAuthPath) return;

    const entityPages = [
      "Orders",
      "Products",
      "Customers",
      "Marketing",
      "Invoices",
      "Transactions",
    ];
    const selectedId =
      initialState?.initialNanoId ||
      initialState?.initialShortId ||
      initialState?.initialProductId ||
      initialState?.initialOrderId ||
      initialState?.initialCustomerId;

    let targetPath = basePath;
    if (selectedId && entityPages.includes(activePage as string)) {
      targetPath = `${basePath}/${selectedId}`;
    }

    if (window.location.pathname !== targetPath) {
      const intent = navigationIntentRef.current;
      if (intent === "push") {
        window.history.pushState(null, "", targetPath);
      } else {
        window.history.replaceState(null, "", targetPath);
      }
    }
    navigationIntentRef.current = "replace";
  }, [activePage, user, initialState, isProfileLoading, isProvisioning]);

  // Browser PopState Lifecycle Listener
  useEffect(() => {
    const handlePopState = () => {
      navigationIntentRef.current = "replace";
      const restoredPage = resolvePageFromPath(window.location.pathname);
      setActivePage(restoredPage);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // GLOBAL ROUTING GUARD
  useEffect(() => {
    if (!user) return;

    // ⚡ RACE CONDITION GUARD: Wait for DB profile before evaluating onboarding status.
    // Without this, the guard fires on the optimistic JWT user (stale defaults)
    // and incorrectly redirects a completed user back to Onboarding.
    // ALSO check isProvisioning to block redirects during workspace setup window.
    if (isProfileLoading || isProvisioning) return;

    console.log("[RouteGuard] Check:", {
      role: user?.role,
      status: user?.onboarding_status,
      page: activePage,
    });

    if (user.role === "admin" || user.role === "super_admin") return;

    // DEF-04 & DEF-06: Unified Admin Route Guard
    if (isAdminPageName(activePage as string)) {
      console.info(
        "[RouteGuard] Non-admin access prevented. Forwarding to Dashboard.",
      );
      setActivePage("Dashboard");
      return;
    }

    // 2. Subscription Status Guard (Trial Expiration)
    const isSubscriptionActive = ["active", "trialing"].includes(
      user.subscription_status || "",
    );
    const isPastDue = user.subscription_status === "past_due";

    if (isPastDue) {
      const allowedBillingPages = [
        "Billing",
        "Settings",
        "Support",
        "Pricing",
        "LoggedOut",
      ];
      if (!allowedBillingPages.includes(activePage)) {
        console.warn(
          "[RouteGuard] Trial Expired / Past Due. Redirecting to Billing.",
        );
        setActivePage("Billing");
        return;
      }
    }

    // 3. Onboarding Guard
    // If user tries to access Dashboard while not onboarded, push back to Onboarding
    // Allow Pricing/Billing/Settings though.
    // FIX: Allow access if subscription is active/trialing, even if 'onboarding_status' isn't explicitly 'completed'
    // This prevents the infinite redirect loop for users who finished setup but didn't get the 'completed' flag.

    if (
      user.onboarding_status !== "completed" &&
      !isSubscriptionActive &&
      !isPastDue
    ) {
      const allowedPages = [
        "Onboarding",
        "Pricing",
        "Billing",
        "Settings",
        "Support",
        "LoggedOut",
        "Channels",
        "Connect Channel",
        "Products",
      ];
      if (!allowedPages.includes(activePage)) {
        console.log("[RouteGuard] Blocking access. Redirecting to Onboarding.");
        setActivePage("Onboarding");
      }
    }
  }, [user, activePage, isProfileLoading]);

  const handleStayActive = () => resetInactivityTimer();

  const handleLogin = async (
    provider: "google" | "apple" | "email",
    credentials?: any,
  ): Promise<{ success: boolean; message: string }> => {
    const result = await login(provider, credentials);
    if (result.success) {
      // Navigation will be handled by useEffect when user state changes
    }
    return result;
  };

  const handleSignUp = async (
    provider: "google" | "apple" | "email",
    data?: any,
  ): Promise<{ success: boolean; message: string }> => {
    const result = await signUp(provider, data);
    if (result.success && provider !== "email") {
      // For social signups, user is automatically logged in
      // For email signup, user needs to verify email first
    }
    return result;
  };

  const handleNavigation = (page: Page, initialStateData: any = {}) => {
    navigationIntentRef.current = "push";
    setInitialState(initialStateData);
    setActivePage(page);
    setIsSidebarOpen(false);
  };

  const handleSearchNavigation = (item: OmniSearchResultItem) => {
    setIsSearchOpen(false);
    if (item.module === "Action" && item.data?.targetPage) {
      handleNavigation(item.data.targetPage, { ...item.data.filter });
      return;
    }
    switch (item.module) {
      case "Orders":
        handleNavigation("Orders", { initialOrderId: item.id });
        break;
      case "Products":
        handleNavigation("Products", { initialProductId: item.id });
        break;
      case "Customers":
        handleNavigation("Customers", { initialCustomerId: item.id });
        break;
      case "Marketing":
        handleNavigation("Marketing", { initialCampaignId: item.id });
        break;
      case "Invoices":
        handleNavigation("Invoices", { initialInvoiceId: item.id });
        break;
      default:
        console.warn(`Navigation for module "${item.module}" not implemented.`);
    }
  };

  const renderUnauthenticatedView = () => {
    return (
      <Suspense fallback={<PageLoader />}>
        {(() => {
          switch (activePage) {
            case "LoggedOut":
              return <LoggedOut onLogin={() => handleNavigation("Login")} />;
            case "SignUp":
              return (
                <SignUp onNavigate={handleNavigation} onSignup={handleSignUp} />
              );
            case "SignUp Welcome":
              return (
                <SignUpWelcome
                  onNavigate={(p) => handleNavigation(p as Page)}
                />
              );
            case "ForgotPassword":
              return <ForgotPassword onNavigate={handleNavigation} />;
            case "AuthCallback":
              return <AuthCallback onNavigate={handleNavigation} />;
            case "Privacy":
              return <Privacy onNavigate={handleNavigation} />;
            case "Terms":
              return <Terms onNavigate={handleNavigation} />;
            case "Track":
              return <Track />;
            case "Onboarding":
              return (
                <div className="flex h-screen items-center justify-center">
                  <div className="text-xl">Setting up your account...</div>
                </div>
              );
            case "Login":
            default:
              // Use the refactored Login page instead of EnhancedLogin
              return (
                <Login onLogin={handleLogin} onNavigate={handleNavigation} />
              );
          }
        })()}
      </Suspense>
    );
  };

  const renderPage = () => {
    if (authStatus === "HYDRATING") {
      return <PageLoader />;
    }
    if (
      authStatus === "AUTHENTICATED_ORPHAN" &&
      activePage !== "Onboarding" &&
      activePage !== "FinalizeWorkspace"
    ) {
      return (
        <WorkspaceRequired onComplete={() => handleNavigation("Onboarding")} />
      );
    }
    const props = { ...initialState };
    return (
      <Suspense fallback={<PageLoader />}>
        {(() => {
          switch (activePage) {
            case "Onboarding":
              return <OnboardingHome onNavigate={handleNavigation} />;
            case "FinalizeWorkspace":
              return <FinalizeWorkspace onNavigate={handleNavigation} />;
            case "Dashboard":
              return (
                <Dashboard
                  setActivePage={handleNavigation}
                  navigateToOrder={(id) =>
                    handleNavigation("Orders", { initialOrderId: id })
                  }
                  currentUser={user}
                />
              );
            case "Orders":
              return <Orders {...props} onNavigate={handleNavigation} />;
            case "Products":
              return <Products {...props} onNavigate={handleNavigation} />;
            case "Customers":
              return <Customers {...props} onNavigate={handleNavigation} />;
            case "Marketing":
              return <Marketing {...props} onNavigate={handleNavigation} />;
            case "Invoices":
              return <Invoices {...props} onNavigate={handleNavigation} />;
            case "Channels":
              return (
                <Channels
                  setActivePage={handleNavigation}
                  currentUser={user!}
                />
              );
            case "Connect Channel":
              return (
                <ConnectChannel onBack={() => handleNavigation("Channels")} />
              );
            case "Reports":
              return <Reports {...props} />;
            case "Shipments":
              return <Shipments />;
            case "Warehouse Inventory":
              return <WarehouseInventory />;
            case "Transactions":
              return <Transactions {...props} onNavigate={handleNavigation} />;
            case "Payment Gateways":
              return <PaymentGateways currentUser={user!} />;
            case "Unified Inbox":
              return <Inbox />;
            case "Knowledge Hub":
              return <KnowledgeHub />;
            case "Settings":
              return <Settings {...props} currentUser={user} />;
            case "Billing":
              return (
                <Billing navigate={(page: Page) => handleNavigation(page)} />
              );
            case "Team Management":
              return <TeamManagement />;
            // Admin Pages
            case "Admin Dashboard":
              return isAdminRole ? (
                <AdminDashboard />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );

            case "Admin Clients":
              return isAdminRole ? (
                <AdminClients
                  setActivePage={(page) => handleNavigation(page as Page)}
                />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Users":
              return isAdminRole ? (
                <AdminUsers
                  setActivePage={(p) => handleNavigation(p as Page)}
                />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Billing":
              return isAdminRole ? (
                <SuperAdminBilling />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Inventory":
              return isAdminRole ? (
                <AdminInventory />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Orders":
              return isAdminRole ? (
                <AdminOrders />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Returns":
              return isAdminRole ? (
                <AdminReturns />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Marketing":
              return isAdminRole ? (
                <AdminMarketing
                  setActivePage={(p) => handleNavigation(p as Page)}
                />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Channels":
              return isAdminRole ? (
                <AdminChannels />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Settings":
              return isAdminRole ? (
                <AdminSettings />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Support":
              return isAdminRole ? (
                <AdminSupport />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Health":
              return isAdminRole ? (
                <HealthDashboard />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Bulk Sync":
              return isAdminRole ? (
                <BulkSyncMonitor />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "DLQ Recovery":
              return isAdminRole ? (
                <DLQConsole />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Admin Logs":
              return isAdminRole ? (
                <AdminLogs />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Operations Center":
              return isAdminRole ? (
                <OperationsCenter />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );
            case "Inventory Reconciliation":
              return isAdminRole ? (
                <InventoryReconciliation />
              ) : (
                <Forbidden onNavigate={handleNavigation} />
              );

            case "Add Client":
              return (
                <AddClient onBack={() => handleNavigation("Admin Clients")} />
              );
            case "Edit Client":
              return (
                <EditClient
                  onSave={() => handleNavigation("Admin Clients")}
                  onCancel={() => handleNavigation("Admin Clients")}
                  clientId={editingClientId!}
                />
              );
            case "Add User":
              return <AddUser onBack={() => handleNavigation("Admin Users")} />;
            case "Create Campaign":
              return (
                <CreateCampaign
                  onBack={() => handleNavigation("Admin Marketing")}
                />
              );
            case "DesignSystem":
              return <DesignSystem />;
            case "Pricing":
              return <PricingPage />;
            case "Support":
              return <ClientSupport />;
            case "Forbidden":
              return <Forbidden onNavigate={handleNavigation} />;
            case "NotAcceptable":
              return <NotAcceptable onNavigate={handleNavigation} />;
            case "ServerError":
              return <ServerError onNavigate={handleNavigation} />;
            case "Privacy":
              return <Privacy onNavigate={handleNavigation} />;
            case "Terms":
              return <Terms onNavigate={handleNavigation} />;
            case "LoggedOut":
              return <LoggedOut onLogin={() => handleNavigation("Login")} />;
            case "Track":
              return <Track />; // Public tracking page (no auth required)
            case "Login":
              return (
                <Dashboard
                  setActivePage={handleNavigation}
                  navigateToOrder={(id) =>
                    handleNavigation("Orders", { initialOrderId: id })
                  }
                  currentUser={user}
                />
              );
            default:
              return <ComingSoon pageName={activePage} />;
          }
        })()}
      </Suspense>
    );
  };

  // 🛡️ CRITICAL UI GATE: Zero-Block Progressive Loading

  // 1. Unauthenticated Gate: Redirect to Login/Signup or Public pages
  if (
    !user &&
    !isAuthLoading &&
    activePage !== "Onboarding" &&
    activePage !== "LoggedOut" &&
    activePage !== "SignUp" &&
    activePage !== "Login" &&
    activePage !== "Track"
  ) {
    // NOTE: We don't render PageLoader here anymore to prevent the 'Red Spinner' hang.
    // The App Shell handles the transition.
  }

  // 2. Error Recovery: Show retry UI if profile fetch failed
  if (lastError && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md w-full text-center border border-red-100">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Connection Issue
          </h2>
          <p className="text-gray-600 mb-6">
            We're having trouble reaching the identity server. Please try again.
          </p>
          <button
            onClick={() => retryProfileFetch()}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if ((!user && activePage !== "Onboarding") || activePage === "LoggedOut") {
    return <>{renderUnauthenticatedView()}</>;
  }

  // 3. Authenticated Shell: Render Sidebar + Header even if loading
  // SENSITIVE GATE: Administratively sensitive UI is ONLY enabled after DB sync confirms role.
  const isAdminUser =
    !isSyncing && ["admin", "super_admin"].includes(user?.role || "");
  const isSyncingRole =
    isSyncing && ["admin", "super_admin"].includes(user?.role || "");
  // const SidebarComponent = isAdminUser ? AdminSidebar : Sidebar;

  // Determine if we should show navigation
  // HIDE if Onboarding
  const showNavigation = activePage !== "Onboarding";

  return (
    <ProvisioningContext.Provider value={{ setIsProvisioning }}>
      <div className="ds-shell">
        {isProvisioning && (
          <ProvisioningOverlay message="Setting up your workspace..." />
        )}
        {showNavigation && (
          <div className="ds-sidebar">
            {isAdminUser ? (
              <AdminSidebar
                activePage={activePage}
                setActivePage={handleNavigation}
                isOpen={isSidebarOpen}
                setIsOpen={setIsSidebarOpen}
                onLogout={handleLogout}
              />
            ) : (
              <Sidebar
                activePage={activePage}
                setActivePage={handleNavigation}
                isOpen={isSidebarOpen}
                setIsOpen={setIsSidebarOpen}
                restricted={false}
                currentUser={user}
                onLogout={handleLogout}
              />
            )}
          </div>
        )}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {showNavigation && (
            <Header
              onMenuButtonClick={() => setIsSidebarOpen(true)}
              onNavigate={handleNavigation}
              onLogout={handleLogout}
              onSearchOpen={() => setIsSearchOpen(true)}
              currentUser={user}
            />
          )}
          <main className="ds-content" style={{ background: "var(--bg-app)" }}>
            {isAuthLoading || isProfileLoading || isSyncing ? (
              <div className="space-y-6 opacity-60">
                <div
                  className="card"
                  style={{ padding: "24px", background: "white" }}
                >
                  <div
                    className="skeleton skeleton-text"
                    style={{ width: "25%" }}
                  ></div>
                  <div className="skeleton skeleton-heading"></div>
                </div>
                <div className="ds-row" style={{ gap: "24px" }}>
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="card"
                      style={{ flex: 1, minWidth: "300px", padding: "24px" }}
                    >
                      <div
                        className="skeleton skeleton-avatar"
                        style={{ marginBottom: "16px" }}
                      ></div>
                      <div
                        className="skeleton skeleton-text"
                        style={{ width: "50%" }}
                      ></div>
                      <div className="skeleton skeleton-text"></div>
                    </div>
                  ))}
                </div>
                {isSyncingRole && (
                  <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1 bg-white border rounded-full shadow-sm">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-tight">
                      Syncing Permissions
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="max-w-[1400px] mx-auto w-full">
                {renderPage()}
              </div>
            )}
          </main>
        </div>
        {!isAdminUser && <AIAssistant onNavigate={handleNavigation} />}
        <InactivityModal
          isOpen={isInactive}
          onStayActive={handleStayActive}
          onLogout={handleLogout}
          countdown={WARNING_TIMEOUT / 1000}
        />
        <OmniSearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          onNavigate={handleSearchNavigation}
        />
        {forbiddenError && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[var(--z-overlay)]">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md border-l-4 border-red-500">
              <h3 className="text-xl font-bold text-red-600 mb-2">
                Access Denied
              </h3>
              <p className="text-gray-700">{forbiddenError}</p>
              <p className="text-sm text-gray-400 mt-4 text-center">
                Redirecting to dashboard...
              </p>
            </div>
          </div>
        )}
      </div>
    </ProvisioningContext.Provider>
  );
}

// Force HMR update
